/**
 * What each marketplace would get, laid out like that marketplace's own
 * listing form: its sections, its field order and labels, the value in each
 * field and, where the label shown isn't what goes out, the code that does.
 * Read-only; it is there to check the translations before anything posts.
 * Built by the API from the same listing the poster sends (@sable/connectors
 * postingForm).
 */
import { useRef, type KeyboardEvent } from "react";
import type { CrosslistTarget, ItemDetail, PostingForm, PostingFormField } from "@sable/api-contract";
import { IconChevron, IconStar } from "../../components/icons.tsx";
import { ErrorBlock, Loading, MarketplaceMark } from "../../components/ui.tsx";
import { currentLocale, t } from "../../i18n/index.ts";
import { useCrosslist } from "../../lib/queries.ts";
import { useWs } from "../../lib/workspace.tsx";
import { useMarketplaceName } from "./panels.tsx";

const majorMoney = (value: number, currency: string) =>
  new Intl.NumberFormat(currentLocale(), { style: "currency", currency }).format(value);

function Photos({ field, form, item }: { field: PostingFormField; form: PostingForm; item: ItemDetail }) {
  const ids = Array.isArray(field.value) ? field.value : [];
  const shown = ids.map((id) => item.photos.find((p) => p.id === id)).filter((p) => p !== undefined);
  if (shown.length === 0) return <div className="pp-box is-empty">{t("posting.noPhotos")}</div>;
  return (
    <div className="stack-sm">
      <div className={`pp-photos${form.photoAspect === 1 ? " is-square" : ""}`}>
        {shown.map((p, i) => (
          <span key={p.id} className="pp-photo">
            <img src={p.thumbUrl} alt="" loading="lazy" />
            {i === 0 && <span className="pp-cover"><IconStar size={11} /></span>}
          </span>
        ))}
      </div>
      <span className="muted small">{t("posting.photosOf", { count: shown.length, limit: form.photoLimit })}</span>
    </div>
  );
}

function Value({ field, form, item }: { field: PostingFormField; form: PostingForm; item: ItemDetail }) {
  const v = field.value;
  if (field.kind === "photos") return <Photos field={field} form={form} item={item} />;
  if (field.state === "empty" || field.state === "not_sent") {
    return <div className={`pp-box is-empty${field.kind === "longtext" ? " is-long" : ""}`}>{field.state === "not_sent" ? t("posting.notSent") : t("posting.empty")}</div>;
  }
  switch (field.kind) {
    case "longtext":
      return <div className="pp-box is-long">{String(v)}</div>;
    case "money":
      return <div className="pp-box is-money num">{typeof v === "number" ? majorMoney(v, form.currency) : String(v)}</div>;
    case "chips":
      return <div className="pp-box is-chips">{(Array.isArray(v) ? v : [String(v)]).map((c) => <span key={c} className="pp-chip">{c}</span>)}</div>;
    case "path":
      return (
        <div className="pp-box is-select">
          <span className="grow">{(Array.isArray(v) ? v : [String(v)]).join(" › ")}</span>
          <IconChevron size={16} />
        </div>
      );
    case "select":
      return <div className="pp-box is-select"><span className="grow">{String(v)}</span><IconChevron size={16} /></div>;
    case "fixed":
      return <div className="pp-box is-fixed">{String(v)}</div>;
    default:
      return <div className="pp-box">{String(v)}</div>;
  }
}

/** `solo`: the section's only field, named like the section, so the label column gives way to the field. */
function FieldRow({ field, form, item, solo }: { field: PostingFormField; form: PostingForm; item: ItemDetail; solo: boolean }) {
  const matched = field.state === "matched_on_post";
  const note = field.kind === "photos" ? undefined : field.note;
  return (
    <div className={`pp-row is-${field.state}${solo ? " is-solo" : ""}`}>
      <div className="pp-label">
        {!solo && <span>{field.label}</span>}
        {field.sent && <code className="pp-key">{field.sent}</code>}
      </div>
      <div className="pp-field">
        <Value field={field} form={form} item={item} />
        {(field.code || matched || note) && (
          <div className="pp-notes">
            {field.code && <span className="pp-code">{t("posting.sentAs", { code: `"${field.code}"` })}</span>}
            {matched && <span className="pp-tag">{note ?? t("posting.onPost")}</span>}
            {!matched && note && <span>{note}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function FormView({ target, item, name }: { target: CrosslistTarget; item: ItemDetail; name: string }) {
  const { form, preview } = target;
  const photoNote = form.sections.flatMap((s) => s.fields).find((f) => f.kind === "photos")?.note;
  return (
    <div className="stack">
      {form.posting === "not_built" && <div className="banner">{t("posting.notBuilt", { marketplace: name })}</div>}
      {preview.blockers.length > 0 && <div className="banner banner-error"><strong>{t("crosslist.blockers")}:</strong> {preview.blockers.join(" · ")}</div>}
      {preview.warnings.length > 0 && <div className="banner"><strong>{t("crosslist.warnings")}:</strong> {preview.warnings.join(" · ")}</div>}
      <div className={`pp pp-${form.marketplace}`}>
        <div className="pp-site">
          <MarketplaceMark code={form.marketplace} />
          <strong>{name}</strong>
          {photoNote && <span className="pp-site-note">{photoNote}</span>}
        </div>
        {form.sections.map((s) => (
          <section key={s.title} className="pp-section">
            <h3 className="pp-section-title">{s.title}</h3>
            {s.fields.map((f) => (
              <FieldRow key={`${s.title}-${f.label}`} field={f} form={form} item={item} solo={s.fields.length === 1 && f.label === s.title} />
            ))}
          </section>
        ))}
      </div>
      <p className="muted small">{t("posting.priceNote")}</p>
    </div>
  );
}

export function PostingPreview({ item, tab, onTab }: { item: ItemDetail; tab: string | null; onTab: (code: string) => void }) {
  const ws = useWs();
  const q = useCrosslist(ws.id, item.id);
  const name = useMarketplaceName();
  const tabs = useRef<HTMLDivElement>(null);
  const targets = q.data?.targets ?? [];
  // Until a tab is picked: where it's live, else somewhere it can post, else the first.
  const current = targets.find((x) => x.marketplace === tab)
    ?? targets.find((x) => x.listing?.status === "active")
    ?? targets.find((x) => x.form.posting === "built")
    ?? targets[0];

  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const i = targets.findIndex((x) => x === current);
    const next = targets[(i + (e.key === "ArrowRight" ? 1 : targets.length - 1)) % targets.length];
    if (!next) return;
    onTab(next.marketplace);
    tabs.current?.querySelector<HTMLButtonElement>(`[data-tab="${next.marketplace}"]`)?.focus();
  };

  return (
    <section className="card" aria-labelledby="pp-title" id="posting-preview">
      <div className="ip-card-head">
        <div>
          <h2 id="pp-title">{t("posting.title")}</h2>
          <p className="muted small">{t("posting.hint")}</p>
        </div>
      </div>
      {q.isPending && <Loading />}
      {q.isError && <ErrorBlock error={q.error} onRetry={() => void q.refetch()} />}
      {current && (
        <>
          <div className="pp-tabs" role="tablist" aria-label={t("posting.title")} ref={tabs} onKeyDown={onKey}>
            {targets.map((x) => {
              const on = x === current;
              const issues = x.preview.blockers.length;
              const checks = x.preview.warnings.length;
              return (
                <button key={x.marketplace} role="tab" data-tab={x.marketplace} id={`pp-tab-${x.marketplace}`}
                  aria-selected={on} aria-controls="pp-panel" tabIndex={on ? 0 : -1}
                  className="pp-tab" onClick={() => onTab(x.marketplace)}>
                  <MarketplaceMark code={x.marketplace} />
                  <span>{name(x.marketplace)}</span>
                  {issues > 0 ? <span className="pp-count is-bad" title={t("posting.issues", { count: issues })}>{issues}</span>
                    : checks > 0 ? <span className="pp-count" title={t("posting.checks", { count: checks })}>{checks}</span> : null}
                </button>
              );
            })}
          </div>
          <div id="pp-panel" role="tabpanel" aria-labelledby={`pp-tab-${current.marketplace}`} className="pp-panel">
            <FormView target={current} item={item} name={name(current.marketplace)} />
          </div>
        </>
      )}
    </section>
  );
}
